import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { plansData, getPlanTotal, formatEur, INVENTARIO_LABELS } from '../data';

const planAccent = {
  bronce: '#8D6E63',
  plata: '#F5D61B',
  oro: '#1A1A1A',
};

const breakdownKeys = ['rotativa', 'digest', 'programas', 'inserts', 'redes', 'produccion'];

const InversionSection = () => {
  const theme = useTheme();

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
          gutterBottom
          color="primary"
          align="center"
        >
          Planes de inversión
        </Typography>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Bronce, Plata y Oro — mensual
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center" sx={{ maxWidth: 720, mx: 'auto' }}>
          Tres niveles de inbound marketing para Canguro: rotativa, programas selectivos, TNO Digest, inserts en
          señal y redes. Tarifas netas mensuales en euros (producción TNO incluida según plan).
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {plansData.map((plan) => {
          const breakdown = getPlanTotal(plan);
          const accent = planAccent[plan.id] || theme.palette.primary.main;
          const headerText = plan.id === 'plata' ? theme.palette.secondary.main : theme.palette.common.white;

          return (
            <Grid item xs={12} md={4} key={plan.id} data-aos="fade-up">
              <Box
                component={Card}
                height="100%"
                display="flex"
                flexDirection="column"
                variant="outlined"
                sx={{
                  borderTop: 4,
                  borderColor: accent,
                  transition: 'all .2s ease-in-out',
                  boxShadow: plan.highlight ? 6 : 1,
                  transform: plan.highlight ? 'scale(1.02)' : 'none',
                  '&:hover': { boxShadow: 8 },
                }}
              >
                <Box
                  py={2}
                  px={2}
                  sx={{
                    bgcolor: accent,
                    color: headerText,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="overline" fontWeight={700} display="block">
                    {plan.tag}
                  </Typography>
                  <Typography variant="h3" fontWeight={900}>
                    {formatEur(breakdown.total)}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ opacity: 0.95 }}>
                    / mes · EUR neto
                  </Typography>
                </Box>
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {plan.title}
                  </Typography>
                  <List dense disablePadding sx={{ flex: 1, mb: 2 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} disableGutters alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                          <CheckCircleIcon sx={{ color: theme.palette.secondary.main }} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box
                    sx={{
                      mt: 'auto',
                      pt: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="textSecondary" display="block">
                      Desglose mensual
                    </Typography>
                    {breakdownKeys.map((key) => (
                      <Typography key={key} variant="body2">
                        {INVENTARIO_LABELS[key]}: <strong>{formatEur(breakdown[key])}</strong>
                      </Typography>
                    ))}
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                    {plan.idealFor}
                  </Typography>
                </CardContent>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={4}>
        <Box
          component={Card}
          sx={{
            bgcolor: theme.palette.alternate.main,
            borderLeft: 4,
            borderColor: theme.palette.primary.main,
          }}
        >
          <CardContent>
            <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
              Comparativa rápida
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Bronce ({formatEur(getPlanTotal({ id: 'bronce' }).total)}):</strong> rotativa + 2 Digest · 1 programa/mes · inserts y redes básicos.
              {' '}
              <strong>Plata ({formatEur(getPlanTotal({ id: 'plata' }).total)}):</strong> rotativa diaria · 8 Digest/mes · 3 programas · inserts con QR ·
              co-publicaciones TNO × Canguro.
              {' '}
              <strong>Oro ({formatEur(getPlanTotal({ id: 'oro' }).total)}):</strong> máximo en señal y On-Demand · 16 Digest · bloques en programas ·
              redes intensivas · producción premium.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              Los montos son tarifas netas mensuales y no incluyen IVA. TNO produce micros e inserts; Canguro aporta
              manual de marca y activos gráficos. Cláusula de continuidad: reposición de pautas ante fuerza mayor en
              planta.
            </Typography>
          </CardContent>
        </Box>
      </Box>
    </Box>
  );
};

export default InversionSection;
